/**
 * Wireframe motifs.
 *
 * Line drawings used as a quiet ground behind section buttons and screens —
 * a photo studio, a stage, the exhibitor grid, the venue plate. They are
 * geometry only: each platform renders the same polylines as SVG, with the same
 * radial mask fading the strokes out before they reach an edge, so nothing ever
 * ends in a hard cut.
 *
 * Everything is authored in a 200x120 box and scaled by the renderer. Points
 * are flat `[x1, y1, x2, y2, ...]` polylines, which is compact and maps
 * directly onto both `<polyline>` and react-native-svg's `Polyline`.
 */

export type Polyline = number[];

export interface WireframeArt {
  /** Authoring box. All geometry is expressed inside it. */
  viewBox: [number, number];
  /** Structural lines — the heavier weight. */
  primary: Polyline[];
  /** Detail lines — lighter, drop out at small sizes. */
  secondary: Polyline[];
  /** Accent-coloured highlights. Kept to two or three per motif. */
  accent: Polyline[];
}

export type WireframeKey =
  | 'photo-ops' | 'special-events' | 'exhibitor-hall' | 'venue-map'
  | 'schedule' | 'talent' | 'quests' | 'badge' | 'search';

const W = 200;
const H = 120;

/** Isometric projection, so the hall and venue motifs read as the 3D map does. */
function iso(x: number, y: number, z: number): [number, number] {
  return [W / 2 + (x - y) * 0.86, 62 + (x + y) * 0.5 - z];
}

function isoRect(x: number, y: number, w: number, d: number, h = 0): Polyline[] {
  const a = iso(x, y, h);
  const b = iso(x + w, y, h);
  const c = iso(x + w, y + d, h);
  const e = iso(x, y + d, h);
  const top: Polyline = [...a, ...b, ...c, ...e, ...a];
  if (h === 0) return [top];
  // Two visible verticals is enough to read as a solid; four is noise.
  const bf = iso(x + w, y, 0);
  const cf = iso(x + w, y + d, 0);
  const ef = iso(x, y + d, 0);
  return [top, [...b, ...bf], [...c, ...cf], [...e, ...ef], [...bf, ...cf, ...ef]];
}

/** A photo studio: backdrop, two lights on stands, camera on a tripod. */
function photoOps(): WireframeArt {
  const primary: Polyline[] = [
    // Backdrop frame and its roll
    [40, 12, 160, 12, 160, 88, 40, 88, 40, 12],
    [40, 22, 160, 22],
    // Camera body and tripod
    [92, 62, 118, 62, 118, 78, 92, 78, 92, 62],
    [105, 78, 105, 96], [105, 96, 94, 108], [105, 96, 116, 108], [105, 96, 105, 108],
  ];
  const secondary: Polyline[] = [
    // Light stands
    [18, 30, 30, 30, 30, 40, 18, 40, 18, 30], [24, 40, 24, 96], [14, 108, 24, 96, 34, 108],
    [170, 30, 182, 30, 182, 40, 170, 40, 170, 30], [176, 40, 176, 96], [166, 108, 176, 96, 186, 108],
    // Floor line
    [8, 96, 192, 96],
  ];
  const accent: Polyline[] = [
    // Lens, and the light throw
    [100, 66, 110, 66, 110, 74, 100, 74, 100, 66],
    [30, 35, 88, 60], [170, 35, 122, 60],
  ];
  return { viewBox: [W, H], primary, secondary, accent };
}

/** A stage with seating banks — the special-events motif. */
function specialEvents(): WireframeArt {
  const primary: Polyline[] = [
    // Stage deck in perspective, and the screen behind it
    [56, 40, 144, 40, 168, 74, 32, 74, 56, 40],
    [64, 10, 136, 10, 136, 38, 64, 38, 64, 10],
  ];
  const secondary: Polyline[] = [];
  // Seating rows, widening toward the viewer
  for (let r = 0; r < 4; r++) {
    const y = 82 + r * 9;
    const spread = 44 + r * 12;
    secondary.push([100 - spread, y, 100 + spread, y]);
    for (let i = -3; i <= 3; i++) {
      const x = 100 + (i * spread) / 3.4;
      secondary.push([x, y - 4, x, y]);
    }
  }
  // Truss
  secondary.push([48, 6, 152, 6], [48, 6, 48, 14], [152, 6, 152, 14]);
  const accent: Polyline[] = [
    [72, 18, 128, 18], [72, 26, 112, 26],
    // Two beams off the truss
    [60, 8, 84, 40], [140, 8, 116, 40],
  ];
  return { viewBox: [W, H], primary, secondary, accent };
}

/** Rows of exhibitor booths, isometric. */
function exhibitorHall(): WireframeArt {
  const primary: Polyline[] = [];
  const secondary: Polyline[] = [];
  const accent: Polyline[] = [];

  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 4; c++) {
      const x = -34 + c * 20;
      const y = -26 + r * 22;
      const cell = isoRect(x, y, 14, 14, 7);
      // One booth in the accent, so the eye has somewhere to land.
      if (r === 1 && c === 2) accent.push(...cell);
      else if (r === 1) primary.push(...cell);
      else secondary.push(...cell);
    }
  }
  // Aisle guides
  secondary.push(...isoRect(-40, -32, 82, 74));
  return { viewBox: [W, H], primary, secondary, accent };
}

/** The venue plate with a grid and a few massing blocks. */
function venueMap(): WireframeArt {
  const primary: Polyline[] = [...isoRect(-42, -34, 84, 68)];
  const secondary: Polyline[] = [];

  // Floor grid
  for (let i = 1; i < 6; i++) {
    const gx = -42 + (84 * i) / 6;
    secondary.push([...iso(gx, -34, 0), ...iso(gx, 34, 0)]);
    const gy = -34 + (68 * i) / 6;
    secondary.push([...iso(-42, gy, 0), ...iso(42, gy, 0)]);
  }

  const accent: Polyline[] = [
    ...isoRect(-26, -18, 22, 14, 11),
    ...isoRect(6, 2, 16, 16, 7),
  ];
  primary.push(...isoRect(-30, 10, 18, 12, 5));
  return { viewBox: [W, H], primary, secondary, accent };
}

/** A schedule grid — rooms down, time across. */
function schedule(): WireframeArt {
  const primary: Polyline[] = [[20, 16, 20, 104], [20, 104, 184, 104]];
  const secondary: Polyline[] = [];
  const accent: Polyline[] = [];
  for (let r = 0; r < 5; r++) {
    const y = 24 + r * 16;
    secondary.push([20, y, 184, y]);
  }
  for (let c = 0; c < 7; c++) {
    const x = 32 + c * 22;
    secondary.push([x, 16, x, 104]);
  }
  // A few blocks sitting on the grid
  primary.push([34, 26, 76, 26, 76, 38, 34, 38, 34, 26]);
  primary.push([100, 42, 156, 42, 156, 54, 100, 54, 100, 42]);
  accent.push([56, 58, 122, 58, 122, 70, 56, 70, 56, 58]);
  accent.push([78, 74, 100, 74, 100, 86, 78, 86, 78, 74]);
  return { viewBox: [W, H], primary, secondary, accent };
}

/** Portrait frames on a wall — the talent motif. */
function talent(): WireframeArt {
  const primary: Polyline[] = [
    [64, 20, 136, 20, 136, 100, 64, 100, 64, 20],
    // Shoulders and head, drawn as an outline
    [86, 100, 88, 78, 112, 78, 114, 100],
    [100, 78, 100, 74],
  ];
  const secondary: Polyline[] = [
    [18, 34, 54, 34, 54, 92, 18, 92, 18, 34],
    [146, 34, 182, 34, 182, 92, 146, 92, 146, 34],
    [26, 92, 28, 76, 44, 76, 46, 92],
    [154, 92, 156, 76, 172, 76, 174, 92],
    [8, 104, 192, 104],
  ];
  const accent: Polyline[] = [
    [88, 52, 112, 52, 112, 74, 88, 74, 88, 52],
    [64, 20, 136, 20],
  ];
  return { viewBox: [W, H], primary, secondary, accent };
}

/** A badge with a tier arc — quests. */
function quests(): WireframeArt {
  const primary: Polyline[] = [
    [74, 24, 126, 24, 126, 88, 100, 104, 74, 88, 74, 24],
  ];
  const secondary: Polyline[] = [
    [60, 12, 140, 12], [60, 12, 60, 20], [140, 12, 140, 20],
    [84, 40, 116, 40], [84, 52, 116, 52], [84, 64, 108, 64],
    [8, 100, 192, 100],
  ];
  const accent: Polyline[] = [
    [88, 74, 112, 74],
    [74, 24, 100, 38, 126, 24],
  ];
  return { viewBox: [W, H], primary, secondary, accent };
}

/** A badge and lanyard — the Me screen. */
function badge(): WireframeArt {
  const primary: Polyline[] = [
    [68, 34, 132, 34, 132, 104, 68, 104, 68, 34],
    [88, 8, 100, 30, 112, 8],
  ];
  const secondary: Polyline[] = [
    [78, 60, 122, 60], [78, 70, 122, 70], [78, 80, 110, 80],
    [8, 104, 192, 104],
  ];
  const accent: Polyline[] = [];
  // A code block, drawn as a coarse grid
  for (let i = 0; i < 4; i++) accent.push([80 + i * 8, 42, 80 + i * 8, 54]);
  accent.push([76, 42, 112, 42], [76, 54, 112, 54]);
  return { viewBox: [W, H], primary, secondary, accent };
}

/** A magnifier over lines of results. */
function search(): WireframeArt {
  const primary: Polyline[] = [];
  const r = 26;
  const ring: Polyline = [];
  for (let a = 0; a <= 32; a++) {
    const th = (a / 32) * Math.PI * 2;
    ring.push(78 + Math.cos(th) * r, 52 + Math.sin(th) * r);
  }
  primary.push(ring, [97, 71, 118, 92]);
  const secondary: Polyline[] = [
    [128, 26, 186, 26], [128, 38, 172, 38],
    [128, 92, 186, 92], [128, 104, 164, 104],
    [14, 92, 62, 92],
  ];
  const accent: Polyline[] = [[66, 44, 90, 44], [66, 56, 82, 56]];
  return { viewBox: [W, H], primary, secondary, accent };
}

const ART: Record<WireframeKey, WireframeArt> = {
  'photo-ops': photoOps(),
  'special-events': specialEvents(),
  'exhibitor-hall': exhibitorHall(),
  'venue-map': venueMap(),
  schedule: schedule(),
  talent: talent(),
  quests: quests(),
  badge: badge(),
  search: search(),
};

export function wireframe(key: WireframeKey): WireframeArt {
  return ART[key];
}

/** Serialises a polyline for an SVG `points` attribute. */
export function pointsOf(line: Polyline): string {
  const out: string[] = [];
  for (let i = 0; i < line.length; i += 2) out.push(`${line[i]},${line[i + 1]}`);
  return out.join(' ');
}
